import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, DatePicker, Space, Typography, Row, Col, Statistic, Spin, Progress, Modal, Form, InputNumber, Select, Button, message } from 'antd';
import { TrophyOutlined, UserOutlined, RiseOutlined, ClockCircleOutlined, AimOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

const targetTypes = [
  { value: 'songs_upload', label: 'Songs Upload' },
  { value: 'posters_upload', label: 'Posters Upload' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'agreements', label: 'Agreements' },
  { value: 'tasks_complete', label: 'Tasks Complete' }
];

export default function StaffPerformance() {
  const [data, setData] = useState({ staff: [], month: '' });
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(dayjs());
  const [targetModal, setTargetModal] = useState(false);
  const [targetForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance', { params: { month: month.format('YYYY-MM') } });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [month]);

  const handleSetTarget = async (values) => {
    try {
      await api.post('/performance/targets', {
        user_id: values.user_id,
        month: month.format('YYYY-MM'),
        target_type: values.target_type,
        target_value: values.target_value
      });
      message.success('Target set!');
      setTargetModal(false);
      targetForm.resetFields();
      fetchData();
    } catch (err) { message.error('Failed'); }
  };

  // Find top performer
  const topPerformer = (data.staff || []).reduce((best, s) => (!best || s.total_actions > best.total_actions) ? s : best, null);

  const columns = [
    {
      title: '#', width: 50,
      render: (_, __, i) => {
        if (i === 0) return <TrophyOutlined style={{ color: '#faad14', fontSize: 18 }} />;
        if (i === 1) return <TrophyOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />;
        if (i === 2) return <TrophyOutlined style={{ color: '#d48806', fontSize: 14 }} />;
        return i + 1;
      }
    },
    {
      title: 'Staff', dataIndex: 'full_name', width: 160,
      render: (v, r) => <span><Text strong>{v}</Text><br/><Tag style={{ fontSize: 10 }}>{r.role}</Tag></span>
    },
    {
      title: 'Attendance', width: 120,
      render: (_, r) => (
        <div>
          <Progress percent={r.attendance_pct} size="small" status={r.attendance_pct >= 80 ? 'success' : r.attendance_pct >= 50 ? 'normal' : 'exception'} />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.present_days}/{r.working_days} days</Text>
        </div>
      )
    },
    { title: 'Late', dataIndex: 'late_arrivals', width: 60, render: v => v > 0 ? <Tag color="red">{v}</Tag> : <Tag color="green">0</Tag> },
    { title: 'Songs', dataIndex: 'songs_uploaded', width: 70, render: v => <Statistic value={v} valueStyle={{ fontSize: 16 }} /> },
    { title: 'Posters', dataIndex: 'posters_uploaded', width: 70, render: v => <Statistic value={v} valueStyle={{ fontSize: 16 }} /> },
    { title: 'Dist.', dataIndex: 'distributions', width: 60, render: v => <Statistic value={v} valueStyle={{ fontSize: 16 }} /> },
    { title: 'Agreements', dataIndex: 'agreements', width: 80, render: v => <Statistic value={v} valueStyle={{ fontSize: 16 }} /> },
    {
      title: 'Tasks', width: 100,
      render: (_, r) => (
        <span>
          <Tag color="green">{r.tasks_completed}</Tag>/<Tag>{r.tasks_assigned}</Tag>
          {r.tasks_assigned > 0 && <Progress percent={Math.round((r.tasks_completed / r.tasks_assigned) * 100)} size="small" showInfo={false} />}
        </span>
      )
    },
    {
      title: 'Total Actions', dataIndex: 'total_actions', width: 100, sorter: (a, b) => a.total_actions - b.total_actions,
      defaultSortOrder: 'descend',
      render: v => <Tag color="blue" style={{ fontSize: 14 }}>{v}</Tag>
    },
    {
      title: 'Targets', width: 150,
      render: (_, r) => {
        if (!r.targets || r.targets.length === 0) return <Text type="secondary">No targets</Text>;
        return r.targets.map((t, i) => {
          const achieved = t.target_type === 'songs_upload' ? r.songs_uploaded :
                          t.target_type === 'posters_upload' ? r.posters_uploaded :
                          t.target_type === 'distribution' ? r.distributions :
                          t.target_type === 'agreements' ? r.agreements :
                          t.target_type === 'tasks_complete' ? r.tasks_completed : 0;
          const pct = t.target_value > 0 ? Math.round((achieved / t.target_value) * 100) : 0;
          return (
            <div key={i} style={{ marginBottom: 4 }}>
              <Text style={{ fontSize: 11 }}>{targetTypes.find(tt => tt.value === t.target_type)?.label}: {achieved}/{t.target_value}</Text>
              <Progress percent={Math.min(pct, 100)} size="small" status={pct >= 100 ? 'success' : 'active'} />
            </div>
          );
        });
      }
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col><Title level={3}><RiseOutlined /> Staff Performance</Title></Col>
        <Col>
          <Space>
            <DatePicker picker="month" value={month} onChange={v => setMonth(v || dayjs())} />
            <Button type="primary" icon={<AimOutlined />} onClick={() => setTargetModal(true)}>Set Target</Button>
          </Space>
        </Col>
      </Row>

      {/* Top stats */}
      {topPerformer && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card style={{ background: 'linear-gradient(135deg, #667eea22, #764ba222)' }}>
              <Statistic
                title={<span><TrophyOutlined style={{ color: '#faad14' }} /> Top Performer</span>}
                value={topPerformer.full_name}
                suffix={<Tag color="gold">{topPerformer.total_actions} actions</Tag>}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total Staff" value={(data.staff || []).length}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#667eea' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total Actions This Month"
                value={(data.staff || []).reduce((s, r) => s + r.total_actions, 0)}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        <Table
          columns={columns}
          dataSource={(data.staff || []).sort((a, b) => b.total_actions - a.total_actions)}
          rowKey="user_id"
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
        />
      )}

      <Modal title="Set Staff Target" open={targetModal} onCancel={() => setTargetModal(false)} footer={null}>
        <Form form={targetForm} onFinish={handleSetTarget} layout="vertical">
          <Form.Item name="user_id" label="Staff" rules={[{ required: true }]}>
            <Select
              placeholder="Select staff" showSearch optionFilterProp="label"
              options={(data.staff || []).map(s => ({ value: s.user_id, label: s.full_name }))}
            />
          </Form.Item>
          <Form.Item name="target_type" label="Target Type" rules={[{ required: true }]}>
            <Select options={targetTypes} />
          </Form.Item>
          <Form.Item name="target_value" label="Target Value" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Set Target</Button>
        </Form>
      </Modal>
    </div>
  );
}
